import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify, mdiPlus, mdiDelete } from '@mdi/js';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
} from 'recharts';
import { ComponentType, ComponentTypeEnum } from '../types';
import { useEscapeKey } from '../hooks/useEscapeKey';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ComponentPickerModalProps {
  componentType: ComponentTypeEnum;
  currentModelId: number;
  currentComponent?: ComponentType;
  currentProperties?: Record<string, any>;
  availableModels: ComponentType[];
  onSelect: (model: ComponentType | null, customProperties?: Record<string, any>) => void;
  onClose: () => void;
}

interface CurvePoint {
  flow: number;
  value: number;
}

type PumpMode = 'curve' | 'fixed';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const NAVY = '#202D61';
const ACCENT_BLUE = '#6d8dbf';

const TYPE_DISPLAY_NAMES: Record<ComponentTypeEnum, string> = {
  pump: 'Pump',
  pipe: 'Pipe',
  branch: 'Branch',
  merge: 'Merge',
  restriction: 'Restriction',
  heat_source: 'Heat Source',
  cooling_package: 'Cooling Package',
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getManufacturer(model: ComponentType): string {
  const explicit = model.manufacturer;
  if (explicit && explicit.trim()) return explicit.trim();

  const name = model.name;
  if (name.startsWith('BSPL')) return 'BSPL';
  if (name.startsWith('BMZ')) return 'BMZ';
  if (name.startsWith('Rexroth') || name.startsWith('Bosch')) return 'Bosch Rexroth';
  return 'Generic';
}

function generateCurveData(
  points: CurvePoint[],
  steps: number = 50,
): { flow: number; value: number }[] {
  if (points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.flow - b.flow);

  if (sorted.length === 1) {
    const p = sorted[0];
    if (p.flow === 0) return [{ flow: 0, value: p.value }];
    const K = p.value / (p.flow * p.flow);
    const maxFlow = p.flow * 3;
    const result: { flow: number; value: number }[] = [];
    for (let i = 0; i <= steps; i++) {
      const q = (maxFlow * i) / steps;
      result.push({ flow: parseFloat(q.toFixed(2)), value: parseFloat((K * q * q).toFixed(2)) });
    }
    return result;
  }

  const minFlow = sorted[0].flow;
  const maxFlow = sorted[sorted.length - 1].flow;
  const range = maxFlow - minFlow || 1;
  const extension = range * 0.2;
  const plotMin = Math.max(0, minFlow - extension);
  const plotMax = maxFlow + extension;

  const interpolate = (q: number): number => {
    if (q <= sorted[0].flow) {
      if (sorted.length < 2) return sorted[0].value;
      const dx = sorted[1].flow - sorted[0].flow;
      if (dx === 0) return sorted[0].value;
      const slope = (sorted[1].value - sorted[0].value) / dx;
      return sorted[0].value + slope * (q - sorted[0].flow);
    }
    if (q >= sorted[sorted.length - 1].flow) {
      const n = sorted.length;
      const dx = sorted[n - 1].flow - sorted[n - 2].flow;
      if (dx === 0) return sorted[n - 1].value;
      const slope = (sorted[n - 1].value - sorted[n - 2].value) / dx;
      return sorted[n - 1].value + slope * (q - sorted[n - 1].flow);
    }
    for (let i = 0; i < sorted.length - 1; i++) {
      if (q >= sorted[i].flow && q <= sorted[i + 1].flow) {
        const dx = sorted[i + 1].flow - sorted[i].flow;
        if (dx === 0) return sorted[i].value;
        const t = (q - sorted[i].flow) / dx;
        return sorted[i].value + t * (sorted[i + 1].value - sorted[i].value);
      }
    }
    return sorted[sorted.length - 1].value;
  };

  const result: { flow: number; value: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const q = plotMin + ((plotMax - plotMin) * i) / steps;
    result.push({
      flow: parseFloat(q.toFixed(2)),
      value: parseFloat(interpolate(q).toFixed(2)),
    });
  }
  return result;
}

function validatePoints(points: CurvePoint[]): string | null {
  if (points.length === 0) return 'At least one data point is required.';
  for (let i = 0; i < points.length; i++) {
    if (points[i].flow <= 0) return `Row ${i + 1}: Flow must be greater than 0.`;
    if (points[i].value < 0) return `Row ${i + 1}: Value must be >= 0.`;
  }
  const flows = points.map((p) => p.flow);
  const uniqueFlows = new Set(flows);
  if (uniqueFlows.size !== flows.length) return 'Duplicate flow values are not allowed.';
  return null;
}

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  width: 760px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* -- Header -------------------------------------------------------- */

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8eaed;
  gap: 12px;
`;

const HeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${NAVY};
  margin: 0;
  white-space: nowrap;
`;

const SearchWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  display: flex;
  align-items: center;
  pointer-events: none;
  color: #9aa0a6;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px 10px 6px 30px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  color: #202124;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: ${ACCENT_BLUE};
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(109, 141, 191, 0.2);
  }

  &::placeholder {
    color: #9aa0a6;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: #f1f3f4;
  }
`;

/* -- Body ---------------------------------------------------------- */

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/* -- Left sidebar -------------------------------------------------- */

const Sidebar = styled.div`
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid #e8eaed;
  overflow-y: auto;
  padding: 8px 0;
`;

const CustomItem = styled.div<{ $active: boolean }>`
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#ffffff' : NAVY)};
  background: ${({ $active }) => ($active ? ACCENT_BLUE : 'transparent')};
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${({ $active }) => ($active ? ACCENT_BLUE : '#f1f3f4')};
  }
`;

const SidebarDivider = styled.div`
  height: 1px;
  background: #e8eaed;
  margin: 6px 0;
`;

const ManufacturerHeader = styled.div`
  padding: 6px 14px 2px;
  font-size: 11px;
  font-weight: 700;
  color: ${NAVY};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModelItem = styled.div<{ $active: boolean }>`
  padding: 5px 14px 5px 24px;
  font-size: 13px;
  color: ${({ $active }) => ($active ? '#ffffff' : '#202124')};
  background: ${({ $active }) => ($active ? ACCENT_BLUE : 'transparent')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.1s;

  &:hover {
    background: ${({ $active }) => ($active ? ACCENT_BLUE : '#f1f3f4')};
  }
`;

const NoResults = styled.div`
  padding: 20px 14px;
  font-size: 12px;
  color: #9aa0a6;
  text-align: center;
`;

/* -- Center pane --------------------------------------------------- */

const CenterPane = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
`;

const EmptyDetail = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa0a6;
  font-size: 13px;
`;

/* Predefined model detail */

const ModelName = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${NAVY};
  margin: 0 0 6px;
`;

const ManufacturerName = styled.div`
  font-size: 13px;
  color: #5f6368;
  margin-bottom: 24px;
`;

const SelectButton = styled.button`
  margin-top: auto;
  align-self: flex-start;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: ${NAVY};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1a2450;
  }

  &:active {
    background: #141d40;
  }
`;

/* Custom editor styles */

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  margin-bottom: 4px;
`;

const RequiredMark = styled.span`
  color: #ea4335;
  margin-left: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #202124;
  border: 1px solid #dadce0;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${ACCENT_BLUE};
    box-shadow: 0 0 0 2px rgba(109, 141, 191, 0.15);
  }

  &::placeholder {
    color: #9aa0a6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #202124;
  border: 1px solid #dadce0;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  background: #fff;

  &:focus {
    border-color: ${ACCENT_BLUE};
    box-shadow: 0 0 0 2px rgba(109, 141, 191, 0.15);
  }
`;

const InfoText = styled.div`
  font-size: 11px;
  color: #9aa0a6;
  margin-top: 4px;
  line-height: 1.4;
`;

const PropertyValue = styled.div`
  font-size: 13px;
  color: #202124;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e8eaed;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${NAVY};
  margin-bottom: 8px;
  margin-top: 4px;
`;

const PointHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: 8px;
  margin-bottom: 4px;
`;

const PointHeaderLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #9aa0a6;
`;

const PointRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const PointInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  font-size: 13px;
  color: #202124;
  border: 1px solid #dadce0;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${ACCENT_BLUE};
    box-shadow: 0 0 0 2px rgba(109, 141, 191, 0.15);
  }
`;

const RemovePointButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;
  padding: 0;

  &:hover {
    background: #fce8e6;
    color: #c5221f;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      background: none;
      color: #5f6368;
    }
  }
`;

const AddPointButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${NAVY};
  background: none;
  border: 1px solid #dadce0;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 4px;

  &:hover {
    background: #f1f3f4;
    border-color: ${NAVY};
  }
`;

const ChartContainer = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
  height: 200px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 4px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #202124;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  background: #fce8e6;
  color: #c5221f;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 14px;
`;

const AddCustomButton = styled.button`
  margin-top: auto;
  align-self: flex-start;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: ${NAVY};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1a2450;
  }

  &:disabled {
    background: #9aa0a6;
    cursor: not-allowed;
  }
`;

/* -- Footer -------------------------------------------------------- */

const Footer = styled.div`
  padding: 10px 16px;
  border-top: 1px solid #e8eaed;
  display: flex;
  align-items: center;
`;

const GenericButton = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  background: none;
  border: 1px solid #dadce0;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f1f3f4;
    border-color: #c6c8cc;
  }
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

// 'custom' is a special selection key for the custom editor
type SidebarSelection = { kind: 'custom' } | { kind: 'model'; id: number };

interface ManufacturerGroup {
  name: string;
  models: ComponentType[];
}

// Types that support custom component definition
const CUSTOM_SUPPORTED_TYPES: ComponentTypeEnum[] = [
  'pump',
  'heat_source',
  'restriction',
  'pipe',
  'branch',
  'merge',
];

export const ComponentPickerModal: React.FC<ComponentPickerModalProps> = ({
  componentType,
  currentModelId,
  currentComponent,
  currentProperties,
  availableModels,
  onSelect,
  onClose,
}) => {
  useEscapeKey(onClose);

  // Detect if we're re-opening an existing custom component (not just a generic one)
  const hasCustomData = currentModelId === 0 && currentComponent && currentProperties && (
    Array.isArray(currentProperties.custom_curve) ||
    currentProperties.fixed_flow_lpm != null ||
    currentComponent.description?.startsWith('Custom:')
  );

  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<SidebarSelection>(
    currentModelId > 0 ? { kind: 'model', id: currentModelId } : { kind: 'custom' },
  );

  // Custom editor state — pump (pre-populate from existing custom data)
  const [pumpName, setPumpName] = useState(() => {
    if (hasCustomData && componentType === 'pump') return currentComponent.name;
    return '';
  });
  const [pumpMode, setPumpMode] = useState<PumpMode>(() => {
    if (hasCustomData && componentType === 'pump' && currentProperties.fixed_flow_lpm != null) return 'fixed';
    return 'curve';
  });
  const [pumpPoints, setPumpPoints] = useState<CurvePoint[]>(() => {
    if (hasCustomData && componentType === 'pump' && Array.isArray(currentProperties.custom_curve)) {
      return currentProperties.custom_curve.map((p: any) => ({
        flow: p.flow_rate ?? 0,
        value: p.pressure_rise ?? 0,
      }));
    }
    return [
      { flow: 0, value: 280 },
      { flow: 25, value: 210 },
      { flow: 50, value: 80 },
    ];
  });
  const [pumpFixedFlow, setPumpFixedFlow] = useState<number>(() => {
    if (hasCustomData && componentType === 'pump') return currentProperties.fixed_flow_lpm ?? 30;
    return 30;
  });

  // Custom editor state — heat_source / restriction (pre-populate from existing custom data)
  const [hsName, setHsName] = useState(() => {
    if (hasCustomData && (componentType === 'heat_source' || componentType === 'restriction')) return currentComponent.name;
    return '';
  });
  const [hsHeatInput, setHsHeatInput] = useState<number>(() => {
    if (hasCustomData && componentType === 'heat_source') return currentProperties.heat_input_kw ?? 5.0;
    return 5.0;
  });
  const [hsPoints, setHsPoints] = useState<CurvePoint[]>(() => {
    if (hasCustomData && (componentType === 'heat_source' || componentType === 'restriction') && Array.isArray(currentProperties.custom_curve)) {
      return currentProperties.custom_curve.map((p: any) => ({
        flow: p.flow_rate ?? 0,
        value: (p.pressure_drop ?? 0) * 10, // kPa -> mbar
      }));
    }
    return [{ flow: 10, value: 300 }];
  });
  const [hsInputPorts, setHsInputPorts] = useState<number>(() => {
    if (hasCustomData && componentType === 'heat_source') return currentComponent.input_ports;
    return 1;
  });
  const [hsOutputPorts, setHsOutputPorts] = useState<number>(() => {
    if (hasCustomData && componentType === 'heat_source') return currentComponent.output_ports;
    return 1;
  });

  // Custom editor state — pipe (pre-populate from existing custom data)
  const [pipeName, setPipeName] = useState(() => {
    if (hasCustomData && componentType === 'pipe') return currentComponent.name;
    return '';
  });
  const [pipeDiameter, setPipeDiameter] = useState<number>(() => {
    if (hasCustomData && componentType === 'pipe') return currentProperties.inner_diameter_mm ?? 20;
    return 20;
  });
  const [pipeLength, setPipeLength] = useState<number>(() => {
    if (hasCustomData && componentType === 'pipe') return currentProperties.length_m ?? 1;
    return 1;
  });

  // Custom editor state — branch/merge (pre-populate from existing custom data)
  const [branchName, setBranchName] = useState(() => {
    if (hasCustomData && (componentType === 'branch' || componentType === 'merge')) return currentComponent.name;
    return '';
  });
  const [branchInputPorts, setBranchInputPorts] = useState<number>(() => {
    if (hasCustomData && (componentType === 'branch' || componentType === 'merge')) return currentComponent.input_ports;
    return componentType === 'merge' ? 2 : 1;
  });
  const [branchOutputPorts, setBranchOutputPorts] = useState<number>(() => {
    if (hasCustomData && (componentType === 'branch' || componentType === 'merge')) return currentComponent.output_ports;
    return componentType === 'merge' ? 1 : 2;
  });

  const [error, setError] = useState<string | null>(null);

  const showCustomOption = CUSTOM_SUPPORTED_TYPES.includes(componentType);

  // Filter models to the requested component type
  const modelsForType = useMemo(
    () => availableModels.filter((m) => m.component_type === componentType),
    [availableModels, componentType],
  );

  // Apply search filter
  const filteredModels = useMemo(() => {
    if (!search.trim()) return modelsForType;
    const q = search.toLowerCase();
    return modelsForType.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.description && m.description.toLowerCase().includes(q)),
    );
  }, [modelsForType, search]);

  // Check if "Custom" matches search
  const customMatchesSearch = useMemo(() => {
    if (!search.trim()) return true;
    return 'custom'.includes(search.toLowerCase());
  }, [search]);

  // Group by manufacturer
  const groups: ManufacturerGroup[] = useMemo(() => {
    const map = new Map<string, ComponentType[]>();
    for (const model of filteredModels) {
      const mfr = getManufacturer(model);
      if (!map.has(mfr)) map.set(mfr, []);
      map.get(mfr)!.push(model);
    }
    return Array.from(map.keys())
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ name: key, models: map.get(key)! }));
  }, [filteredModels]);

  // Resolve selected model
  const selectedModel = useMemo(() => {
    if (selection.kind !== 'model') return null;
    return modelsForType.find((m) => m.id === selection.id) ?? null;
  }, [modelsForType, selection]);

  // Curve data for custom editors
  const hsCurveData = useMemo(() => generateCurveData(hsPoints), [hsPoints]);
  const pumpCurveData = useMemo(() => generateCurveData(pumpPoints), [pumpPoints]);

  /* ---- point mutation helpers ---- */
  const updatePoint = (
    setter: React.Dispatch<React.SetStateAction<CurvePoint[]>>,
    index: number,
    field: 'flow' | 'value',
    raw: string,
  ) => {
    const parsed = parseFloat(raw);
    setter((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: isNaN(parsed) ? 0 : parsed } : p)),
    );
  };

  const addPoint = (setter: React.Dispatch<React.SetStateAction<CurvePoint[]>>) => {
    setter((prev) => [...prev, { flow: 0, value: 0 }]);
  };

  const removePoint = (
    setter: React.Dispatch<React.SetStateAction<CurvePoint[]>>,
    index: number,
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  /* ---- handlers ---- */

  const handleSelectModel = () => {
    if (selectedModel) {
      onSelect(selectedModel);
    }
  };

  const handleUseGeneric = () => {
    onSelect(null);
  };

  const handleAddCustom = () => {
    setError(null);

    if (componentType === 'pump') {
      if (!pumpName.trim()) {
        setError('Component name is required.');
        return;
      }
      if (pumpMode === 'curve') {
        const pointError = validatePoints(pumpPoints);
        if (pointError) {
          setError(pointError);
          return;
        }
        const customModel: ComponentType = {
          id: 0,
          name: pumpName.trim(),
          component_type: 'pump',
          component_type_display: 'Custom Pump',
          description: `Custom: ${pumpName.trim()}`,
          icon: 'pump',
          input_ports: 1,
          output_ports: 1,
          default_properties: {},
          created_at: '',
          updated_at: '',
        };
        onSelect(customModel, {
          custom_curve: pumpPoints.map((p) => ({
            flow_rate: p.flow,
            pressure_rise: p.value,
          })),
        });
      } else {
        const customModel: ComponentType = {
          id: 0,
          name: pumpName.trim(),
          component_type: 'pump',
          component_type_display: 'Fixed Flow Pump',
          description: `Custom: ${pumpName.trim()}`,
          icon: 'pump',
          input_ports: 1,
          output_ports: 1,
          default_properties: {},
          created_at: '',
          updated_at: '',
        };
        onSelect(customModel, {
          fixed_flow_lpm: pumpFixedFlow,
        });
      }
    } else if (componentType === 'heat_source' || componentType === 'restriction') {
      if (!hsName.trim()) {
        setError('Component name is required.');
        return;
      }
      const pointError = validatePoints(hsPoints);
      if (pointError) {
        setError(pointError);
        return;
      }
      const isHeatSource = componentType === 'heat_source';
      const customModel: ComponentType = {
        id: 0,
        name: hsName.trim(),
        component_type: componentType,
        component_type_display: isHeatSource ? 'Custom Heat Source' : 'Custom Restriction',
        description: `Custom: ${hsName.trim()}`,
        icon: componentType,
        input_ports: isHeatSource ? hsInputPorts : 1,
        output_ports: isHeatSource ? hsOutputPorts : 1,
        default_properties: {},
        created_at: '',
        updated_at: '',
      };
      const props: Record<string, any> = {
        custom_curve: hsPoints.map((p) => ({
          flow_rate: p.flow,
          pressure_drop: p.value * 0.1, // mbar -> kPa
        })),
      };
      if (isHeatSource) {
        props.heat_input_kw = hsHeatInput;
      }
      onSelect(customModel, props);
    } else if (componentType === 'pipe') {
      if (!pipeName.trim()) {
        setError('Component name is required.');
        return;
      }
      const customModel: ComponentType = {
        id: 0,
        name: pipeName.trim(),
        component_type: 'pipe',
        component_type_display: 'Custom Pipe',
        description: `Custom: ${pipeName.trim()}`,
        icon: 'pipe',
        input_ports: 1,
        output_ports: 1,
        default_properties: {},
        created_at: '',
        updated_at: '',
      };
      onSelect(customModel, {
        inner_diameter_mm: pipeDiameter,
        length_m: pipeLength,
      });
    } else if (componentType === 'branch' || componentType === 'merge') {
      if (!branchName.trim()) {
        setError('Component name is required.');
        return;
      }
      const isMerge = componentType === 'merge';
      const customModel: ComponentType = {
        id: 0,
        name: branchName.trim(),
        component_type: componentType,
        component_type_display: isMerge ? 'Custom Merge' : 'Custom Branch',
        description: `Custom: ${branchName.trim()}`,
        icon: componentType,
        input_ports: isMerge ? branchInputPorts : 1,
        output_ports: isMerge ? 1 : branchOutputPorts,
        default_properties: {},
        created_at: '',
        updated_at: '',
      };
      onSelect(customModel, {});
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  /* ---- renderers ---- */

  const renderPointTable = (
    points: CurvePoint[],
    setter: React.Dispatch<React.SetStateAction<CurvePoint[]>>,
    flowLabel: string,
    valueLabel: string,
  ) => (
    <>
      <PointHeaderRow>
        <PointHeaderLabel>{flowLabel}</PointHeaderLabel>
        <PointHeaderLabel>{valueLabel}</PointHeaderLabel>
        <PointHeaderLabel />
      </PointHeaderRow>
      {points.map((pt, idx) => (
        <PointRow key={idx}>
          <PointInput
            type="number"
            step="any"
            value={pt.flow}
            onChange={(e) => updatePoint(setter, idx, 'flow', e.target.value)}
          />
          <PointInput
            type="number"
            step="any"
            value={pt.value}
            onChange={(e) => updatePoint(setter, idx, 'value', e.target.value)}
          />
          <RemovePointButton
            onClick={() => removePoint(setter, idx)}
            disabled={points.length <= 1}
            title="Remove point"
          >
            <Icon path={mdiDelete} size={0.6} />
          </RemovePointButton>
        </PointRow>
      ))}
      <AddPointButton onClick={() => addPoint(setter)}>
        <Icon path={mdiPlus} size={0.55} />
        Add Point
      </AddPointButton>
    </>
  );

  const renderChart = (
    curveData: { flow: number; value: number }[],
    points: CurvePoint[],
    xLabel: string,
    yLabel: string,
  ) => {
    const sortedPoints = [...points].sort((a, b) => a.flow - b.flow);
    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={curveData} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
            <XAxis
              dataKey="flow"
              type="number"
              domain={['dataMin', 'dataMax']}
              label={{
                value: xLabel,
                position: 'insideBottom',
                offset: -10,
                style: { fontSize: 11, fill: '#5f6368' },
              }}
              tick={{ fontSize: 10, fill: '#5f6368' }}
            />
            <YAxis
              dataKey="value"
              type="number"
              label={{
                value: yLabel,
                angle: -90,
                position: 'insideLeft',
                offset: -5,
                style: { fontSize: 11, fill: '#5f6368', textAnchor: 'middle' },
              }}
              tick={{ fontSize: 10, fill: '#5f6368' }}
            />
            <Tooltip
              formatter={(val) => (typeof val === 'number' ? val.toFixed(1) : val)}
              labelFormatter={(val) => `Flow: ${val}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#1a73e8"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            {sortedPoints.map((pt, idx) => (
              <ReferenceDot
                key={idx}
                x={pt.flow}
                y={pt.value}
                r={5}
                fill="#1a73e8"
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    );
  };

  const renderPredefinedDetail = () => {
    if (!selectedModel) {
      return (
        <EmptyDetail>
          {search.trim()
            ? 'No models match your search.'
            : 'No models available for this component type.'}
        </EmptyDetail>
      );
    }
    return (
      <CenterPane>
        <ModelName>{selectedModel.name}</ModelName>
        <ManufacturerName>{getManufacturer(selectedModel)}</ManufacturerName>
        <SelectButton onClick={handleSelectModel}>Select This Model</SelectButton>
      </CenterPane>
    );
  };

  const renderCustomPump = () => (
    <>
      <FormGroup>
        <Label>
          Component Name
          <RequiredMark>*</RequiredMark>
        </Label>
        <Input
          type="text"
          value={pumpName}
          onChange={(e) => setPumpName(e.target.value)}
          placeholder="e.g. Main coolant pump"
        />
      </FormGroup>

      <FormGroup>
        <Label>Pump Mode</Label>
        <RadioGroup>
          <RadioLabel>
            <input
              type="radio"
              name="pumpMode"
              checked={pumpMode === 'curve'}
              onChange={() => setPumpMode('curve')}
            />
            Pump curve
          </RadioLabel>
          <RadioLabel>
            <input
              type="radio"
              name="pumpMode"
              checked={pumpMode === 'fixed'}
              onChange={() => setPumpMode('fixed')}
            />
            Fixed flow
          </RadioLabel>
        </RadioGroup>
      </FormGroup>

      {pumpMode === 'curve' ? (
        <FormGroup>
          <SectionTitle>Pressure Rise Characteristic</SectionTitle>
          {renderPointTable(pumpPoints, setPumpPoints, 'Flow [l/min]', 'Pressure Rise [kPa]')}
          {renderChart(pumpCurveData, pumpPoints, 'Flow [l/min]', 'Pressure Rise [kPa]')}
        </FormGroup>
      ) : (
        <FormGroup>
          <Label>Fixed Flow Rate [l/min]</Label>
          <Input
            type="number"
            step="any"
            value={pumpFixedFlow}
            onChange={(e) => setPumpFixedFlow(parseFloat(e.target.value) || 0)}
          />
          <InfoText>
            Solver will calculate pressure drops. Result shows the required pump head.
          </InfoText>
        </FormGroup>
      )}
    </>
  );

  const renderCustomHeatSource = () => {
    const isRestriction = componentType === 'restriction';
    return (
      <>
        <FormGroup>
          <Label>
            Component Name
            <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            type="text"
            value={hsName}
            onChange={(e) => setHsName(e.target.value)}
            placeholder={isRestriction ? 'e.g. Throttle valve' : 'e.g. Intercooler'}
          />
        </FormGroup>

        {!isRestriction && (
          <FormGroup>
            <Label>Default Heat Input [kW]</Label>
            <Input
              type="number"
              step="any"
              value={hsHeatInput}
              onChange={(e) => setHsHeatInput(parseFloat(e.target.value) || 0)}
            />
            <InfoText>Can be overridden per operating scenario</InfoText>
          </FormGroup>
        )}

        <FormGroup>
          <SectionTitle>Pressure Drop Characteristic</SectionTitle>
          {renderPointTable(hsPoints, setHsPoints, 'Flow [l/min]', 'Pressure Drop [mbar]')}
          {renderChart(hsCurveData, hsPoints, 'Flow [l/min]', 'Pressure Drop [mbar]')}
        </FormGroup>

        {!isRestriction && (
          <>
            <FormGroup>
              <Label>Input Ports</Label>
              <Select
                value={hsInputPorts}
                onChange={(e) => setHsInputPorts(parseInt(e.target.value, 10))}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Output Ports</Label>
              <Select
                value={hsOutputPorts}
                onChange={(e) => setHsOutputPorts(parseInt(e.target.value, 10))}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </Select>
            </FormGroup>
          </>
        )}
      </>
    );
  };

  const renderCustomPipe = () => (
    <>
      <FormGroup>
        <Label>
          Component Name
          <RequiredMark>*</RequiredMark>
        </Label>
        <Input
          type="text"
          value={pipeName}
          onChange={(e) => setPipeName(e.target.value)}
          placeholder="e.g. Main supply line"
        />
      </FormGroup>

      <FormGroup>
        <Label>Inner Diameter [mm]</Label>
        <Input
          type="number"
          step="any"
          value={pipeDiameter}
          onChange={(e) => setPipeDiameter(parseFloat(e.target.value) || 0)}
        />
      </FormGroup>

      <FormGroup>
        <Label>Length [m]</Label>
        <Input
          type="number"
          step="any"
          value={pipeLength}
          onChange={(e) => setPipeLength(parseFloat(e.target.value) || 0)}
        />
      </FormGroup>
    </>
  );

  const renderCustomBranchMerge = () => {
    const isMerge = componentType === 'merge';
    return (
      <>
        <FormGroup>
          <Label>
            Component Name
            <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            type="text"
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            placeholder={isMerge ? 'e.g. Main merge' : 'e.g. Main splitter'}
          />
        </FormGroup>

        <FormGroup>
          <Label>Input Ports</Label>
          {isMerge ? (
            <Select
              value={branchInputPorts}
              onChange={(e) => setBranchInputPorts(parseInt(e.target.value, 10))}
            >
              {[2, 3, 4, 5, 6, 7, 8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
          ) : (
            <PropertyValue>1</PropertyValue>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Output Ports</Label>
          {isMerge ? (
            <PropertyValue>1</PropertyValue>
          ) : (
            <Select
              value={branchOutputPorts}
              onChange={(e) => setBranchOutputPorts(parseInt(e.target.value, 10))}
            >
              {[2, 3, 4, 5, 6, 7, 8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
          )}
        </FormGroup>
      </>
    );
  };

  const renderCustomEditor = () => {
    let editor: React.ReactNode;
    switch (componentType) {
      case 'pump':
        editor = renderCustomPump();
        break;
      case 'heat_source':
      case 'restriction':
        editor = renderCustomHeatSource();
        break;
      case 'pipe':
        editor = renderCustomPipe();
        break;
      case 'branch':
      case 'merge':
        editor = renderCustomBranchMerge();
        break;
      default:
        editor = null;
    }

    return (
      <CenterPane>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {editor}
        <AddCustomButton onClick={handleAddCustom}>
          {hasCustomData ? 'Save Custom' : 'Add Custom'}
        </AddCustomButton>
      </CenterPane>
    );
  };

  const typeLabel = TYPE_DISPLAY_NAMES[componentType] || componentType;

  return ReactDOM.createPortal(
    <Overlay onClick={handleOverlayClick}>
      <Panel onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Header>
          <HeaderTitle>Select {typeLabel}</HeaderTitle>
          <SearchWrapper>
            <SearchIcon>
              <Icon path={mdiMagnify} size={0.6} />
            </SearchIcon>
            <SearchInput
              placeholder="Search models..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </SearchWrapper>
          <CloseButton onClick={onClose} title="Close">
            <Icon path={mdiClose} size={0.7} />
          </CloseButton>
        </Header>

        {/* Body: sidebar + center */}
        <Body>
          {/* Left sidebar */}
          <Sidebar>
            {showCustomOption && customMatchesSearch && (
              <>
                <CustomItem
                  $active={selection.kind === 'custom'}
                  onClick={() => {
                    setSelection({ kind: 'custom' });
                    setError(null);
                  }}
                >
                  Custom
                </CustomItem>
                <SidebarDivider />
              </>
            )}

            {groups.length === 0 && filteredModels.length === 0 && (
              <NoResults>No models match your search.</NoResults>
            )}
            {groups.map((group) => (
              <React.Fragment key={group.name}>
                <ManufacturerHeader>{group.name}</ManufacturerHeader>
                {group.models.map((model) => (
                  <ModelItem
                    key={model.id}
                    $active={selection.kind === 'model' && selection.id === model.id}
                    onClick={() => setSelection({ kind: 'model', id: model.id })}
                    title={model.name}
                  >
                    {model.name}
                  </ModelItem>
                ))}
              </React.Fragment>
            ))}
          </Sidebar>

          {/* Center pane */}
          {selection.kind === 'custom' ? renderCustomEditor() : renderPredefinedDetail()}
        </Body>

        {/* Footer */}
        <Footer>
          <GenericButton onClick={handleUseGeneric}>Use Generic (no curve data)</GenericButton>
        </Footer>
      </Panel>
    </Overlay>,
    document.body,
  );
};
